import React from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import type { LimitShortfall } from 'fym-shared/src/music24/limitUpsell';
import { TypedMemo } from '../../common/types';
import { LimitUpsellCard, type LimitUpsellAction, type LimitUpsellFit } from './LimitUpsellCard';
import type { LimitUpsellContext } from './limitUpsellCopy';
import { limitUpsellMessages as messages } from './limitUpsellMessages';
import { useLimitUpsell } from './useLimitUpsell';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the limit upsell; each part uses its own slice of it */
import styles from './LimitUpsell.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface LimitUpsellProps {
  /** The shortfall of the flow now; null when it fits (then only the "added" line can show). */
  shortfall: LimitShortfall | null;
  context: LimitUpsellContext;
  fit?: LimitUpsellFit | null;
  extraActions?: LimitUpsellAction[];
  /** After a successful add-on: the flow reads its numbers again (the cache tags go already). */
  onAdded?: () => void;
  className?: string;
}

/**
 * The limit upsell of a flow (design limit-upsell). Keep it mounted for the life of the flow: it
 * holds the add-on state, so the green "added" line (a4) stays after the shortfall goes.
 */
const LimitUpsellComponent: React.FC<LimitUpsellProps> = ({
  shortfall,
  context,
  fit,
  extraActions,
  onAdded,
  className,
}) => {
  const intl = useIntl();
  const state = useLimitUpsell(shortfall, onAdded);
  if (shortfall === null && state.addState !== 'added') {
    return null;
  }
  return (
    <div className={clsx('d-flex flex-column gap-3', className)}>
      {state.addState === 'added' && state.added !== null && (
        <div className={clsx(styles.alert, styles.alertOk)} role="status" data-testid="limit-upsell-added">
          {intl.formatMessage(state.added.kind === 'videoCredits' ? messages.addedCredits : messages.addedPosts, {
            count: state.added.count,
          })}
        </div>
      )}
      {shortfall !== null && (
        <LimitUpsellCard shortfall={shortfall} context={context} state={state} fit={fit} extraActions={extraActions} />
      )}
    </div>
  );
};

export const LimitUpsell = TypedMemo(LimitUpsellComponent);
